import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Field, useField } from 'react-final-form';
import { connect } from 'react-redux';

import { ICountry } from 'APIPackage/CatalogsModule/interfaces/ICountry';
import { ITargetEntity } from 'APIPackage/DictionariesModule/interfaces/ITargetEntity';
import { ILegalEntityLookup } from 'APIPackage/FnsModule/interfaces/ILegalEntityLookup';
import { RafpRequestStatusTypeEnum } from 'APIPackage/KnmModule/enums/RafpRequestStatusTypeEnum';
import { IKnmCheckSubjectResult } from 'APIPackage/KnmModule/interfaces/IKnmCheckSubjectResult';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { KnmSubjectTypesEnum } from 'KnmErknmPackage/ErknmSubjectModule/components/ErknmSubjectInfo';
import {
  COUNTRY_BLOCK_ID,
  DOCUMENT_BLOCK_ID,
  KPP_BLOCK_ID,
  ORGANIZATION_NAME_BLOCK_ID,
  RAFP_NZA_BLOCK_ID,
  SNILS_BLOCK_ID,
} from 'KnmPackage/KnmSubjectModule/constants/knmSubjectFieldsElementsIds';
import { KnmInnField } from 'KnmPackage/MainModule/components/KnmInnField';
import { KnmOgrnField } from 'KnmPackage/MainModule/components/KnmOgrnField';
import { KnmSnilsField } from 'KnmPackage/MainModule/components/KnmSnilsField';
import { KnmSonkoField } from 'KnmPackage/MainModule/components/KnmSonkoField';
import { INN_LABEL_BLOCK_ID, OGRN_LABEL_BLOCK_ID } from 'KnmPackage/MainModule/constants/fieldsElementsIds';
import { useKnmSetFnsLookupLoadingFlags } from 'KnmPackage/MainModule/hooks/useKnmSetFnsLookupLoadingFlags';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { SelectInputFieldAutoOptions } from 'UIPackage/MainModule/components/SelectInputFieldAutoOptions';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { TextareaField } from 'UIPackage/MainModule/components/TextareaField';
import { IKnmSubject248ItAccreditationResponse } from 'APIPackage/KnmModule/interfaces/IKnmSubject248ItAccreditationResponse';
import { parsePassport } from 'CorePackage/MainModule/common/parsePassport';
import { Hint } from 'CorePackage/MainModule/components/Hint';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { ICommonFormRef } from 'KnmErknmPackage/MainModule/interfaces/ICommonFormRef';
import { getDistrictSelectOptions } from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { IKnmDistrictSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmDistrictSelectOption';
import { rafpNotFoundMessage } from 'KnmErknmPackage/ErknmSubjectModule/constants/erknmSubjectConstants';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { getKnmDataCreated } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { getDecisionDataCreatedAt } from 'DecisionPackage/MainModule/selectors/decisionSelectors';

import { KnmMspField } from '../../../KnmPackage/MainModule/components/KnmMspField';
import {
  isFnsAddressFnsError,
  isOrganizationNameFnsError,
  isWorkAddressFnsError,
  renderFnsCheckMessage,
  renderFnsErvkEqualsError,
} from '../../../CorePackage/MainModule/common/renderFnsMessage';
import { KnmSubjectItInfo } from '../../../KnmPackage/MainModule/components/KnmSubjectItInfo';
import { ErknmRegistryCheck } from '../../../KnmErknmPackage/ErknmSubjectModule/components/ErknmRegistryCheck';
import { KnmSubjectNkoInfo } from '../../../KnmPackage/MainModule/components/KnmSubjectNkoInfo';
import styles from './DecisionSubjectItem.module.scss';
import { KnmFiasAddressField } from '../../../KnmPackage/MainModule/components/KnmFiasAddressField';
import { ErknmFieldMark } from '../../../KnmErknmPackage/MainModule/components/ErknmFieldMark';
import { getTitleFieldWithRedStar } from '../../../functions/getTitleFieldWithRedStar';
import { IRootState } from '../../../CorePackage/MainModule/interfaces/IRootState';
import { useDecisionErknmSubjectFields } from '../hooks/useDecisionErknmSubjectFields';
import { useDecisionErknmSubjectHandlers } from '../hooks/useDecisionErknmSubjectHandlers';

export interface IDecisionSubjectItemProps {
  checkSubjectResult?: IKnmCheckSubjectResult | null;
  ervkId: string | undefined;
  formRef: ICommonFormRef | undefined;
  getBlockId: (id: string) => string;
  inn: string | undefined;
  isFiz: boolean | undefined;
  isIt?: boolean;
  isSonko?: boolean;
  isWatchOnly: boolean;
  itAccreditationResponse: IKnmSubject248ItAccreditationResponse | undefined;
  kpp: string | undefined;
  mspCode?: string;
  mspRegistryDate?: string;
  name?: string;
  ogrn: string | undefined;
  organizationAutocomplete?: boolean;
  organizationType: IDictionarySelectOption<ITargetEntity> | undefined;
  rafpNza: string | undefined;
  smevItAccreditationRequestStatus: string | undefined;
  smevRequestStatus: RafpRequestStatusTypeEnum | undefined;
  snils: string | undefined;
  subjectTypeLabel?: string;
  subjectTypeOptions?: IDictionarySelectOption<ITargetEntity>[];
}

export interface IDecisionSubjectItemRef {
  onClear: () => void;
}

interface IDecisionSubjectItemStoreProps {
  districtSelectOptions: IKnmDistrictSelectOption[];
  settings: IAdminSetting[];
  created: string | null | undefined;
  decisionDataCreated?: string | null;
}

const DecisionSubjectItemWithoutStore = forwardRef<
  IDecisionSubjectItemRef,
  IDecisionSubjectItemProps & IDecisionSubjectItemStoreProps
>(
  (
    {
      checkSubjectResult,
      ervkId,
      formRef,
      getBlockId,
      inn,
      isFiz,
      isIt,
      isSonko,
      isWatchOnly,
      itAccreditationResponse,
      kpp,
      mspCode,
      mspRegistryDate,
      name: parentName,
      ogrn,
      organizationAutocomplete,
      organizationType,
      rafpNza,
      smevRequestStatus,
      snils,
      subjectTypeLabel,
      subjectTypeOptions,
      smevItAccreditationRequestStatus,
      settings,
      districtSelectOptions,
      created,
      decisionDataCreated,
    },
    ref,
  ) => {
    const name = parentName ? `${parentName}.` : '';
    const nameWithoutDot = name.endsWith('.') ? name.slice(0, -1) : name;
    const subjectFieldNames = useDecisionErknmSubjectFields({ name: nameWithoutDot });

    const subjectTypeBlockId = getBlockId(subjectFieldNames.organizationType);
    const organizationNameBlockId = getBlockId(ORGANIZATION_NAME_BLOCK_ID);
    const countryBlockId = getBlockId(COUNTRY_BLOCK_ID);
    const documentBlockId = getBlockId(DOCUMENT_BLOCK_ID);
    const innLabelBlockId = getBlockId(INN_LABEL_BLOCK_ID);
    const kppBlockId = getBlockId(KPP_BLOCK_ID);
    const ogrnLabelBlockId = getBlockId(OGRN_LABEL_BLOCK_ID);
    const rafpNzaBlockId = getBlockId(RAFP_NZA_BLOCK_ID);
    const snilsBlockId = getBlockId(SNILS_BLOCK_ID);

    const isRafpNzaSent = smevRequestStatus === RafpRequestStatusTypeEnum.REQUEST_SENT;
    const isRafpNzaNotFound = smevRequestStatus === RafpRequestStatusTypeEnum.NOT_FOUND;
    const isRafpNzaError = smevRequestStatus === RafpRequestStatusTypeEnum.ERROR_RETRY;
    const isFIZ = organizationType?.label === KnmSubjectTypesEnum.FIZ;
    const isUR = organizationType?.label === KnmSubjectTypesEnum.UR;
    const isIP = organizationType?.label === KnmSubjectTypesEnum.IP;
    const isIFIZ = organizationType?.label === KnmSubjectTypesEnum.IFIZ;
    const isFIUR = organizationType?.label === KnmSubjectTypesEnum.FIUR;
    const isIUR = organizationType?.label === KnmSubjectTypesEnum.IUR;

    const isLaterThanHideFields4129 = isLaterThanAdminSetting(
      created || decisionDataCreated,
      settings,
      AdminSettingCodesEnum.SETTING_HIDE_FIELDS_4_1_29,
    );

    const { setFnsLookupByOgrnLoadingFlag, setFnsLookupByInnLoadingFlag } = useKnmSetFnsLookupLoadingFlags();
    const [lookupData, setLookupData] = useState<ILegalEntityLookup | boolean | undefined>(undefined);
    const [checkByInnMessage, setCheckByInnMessage] = useState<string | undefined>(undefined);

    const isGeneralFieldDisabled = isWatchOnly || !!ervkId;
    const isOrganizationNameFieldEnabled = !isGeneralFieldDisabled && isFiz;
    const isShowAutoCompleteField = isUR || isIP;

    const normalizeFio = (value: string | undefined) => {
      if (!value) return value;

      if (isFIZ) {
        return value.replace(/[^а-яё\d- ]/gi, '');
      }
      if (isIFIZ) {
        return value.replace(/[^а-яёa-z-`' ]/gi, '');
      }

      return value;
    };

    const [rafpNzaNotFoundText, setRafpNzaNotFoundText] = useState<string | undefined>(rafpNotFoundMessage);

    const {
      subjectTypeChange,
      onClear,
      handleLegalEntityAutoCompleteSelect,
      handleAutoCompleteClear,
      handleRafpNzaChange,
    } = useDecisionErknmSubjectHandlers({
      formRef,
      subjectFieldNames,
      isUR,
      isIP,
      setCheckByInnMessage,
      rafpNza,
      setRafpNzaNotFoundText,
      isRafpNzaNotFound,
      rafpNotFoundMessage,
      districtSelectOptions,
    });

    useImperativeHandle(
      ref,
      () => {
        return {
          onClear,
        };
      },
      [],
    );

    const isRafpFilled = !!rafpNza && /^\d{11}$/.test(rafpNza);
    const saveKnmAndWaitFromRafpWarning =
      (isRafpFilled || (inn && kpp)) && !smevRequestStatus
        ? 'Сохраните Решение и ожидайте загрузки информации из РАФП'
        : undefined;
    const waitFromRafpWarning = isRafpNzaSent ? 'Ожидайте загрузки информации из РАФП' : undefined;
    const rafpWarning = isFIUR ? saveKnmAndWaitFromRafpWarning || waitFromRafpWarning : undefined;
    const innOrSnilsWarning = isFiz && !inn && !snils ? 'Необходимо заполнить либо ИНН, либо СНИЛС' : undefined;
    const filterUlCountries = (values: ICountry[]) => values?.filter(item => item.ulEnabled);
    const filterFlCountries = (values: ICountry[]) => values?.filter(item => item.flEnabled);

    const locationnotInFIAS = useField(`${name}locationnotInFIAS`, { subscription: { value: true } }).input.value;

    useEffect(() => {
      if (!formRef?.current) return;

      if (locationnotInFIAS) {
        formRef.current.change(`${name}locationfiasRegion` as keyof IKnmForm);
      }
    }, [locationnotInFIAS, formRef, name]);

    const isInnDisabled =
      isGeneralFieldDisabled || organizationAutocomplete || (isFIUR && (!!rafpNza || isRafpNzaSent));

    const isInnWarning =
      innOrSnilsWarning ||
      rafpWarning ||
      (!isFiz && !smevRequestStatus && renderFnsCheckMessage(checkSubjectResult, lookupData, organizationAutocomplete));

    return (
      <>
        <Field name={subjectFieldNames.checkSubjectResult}>{() => null}</Field>
        <Field name={subjectFieldNames.dateCreate}>{() => null}</Field>
        <Field name={subjectFieldNames.firstOrganizationName}>{() => null}</Field>
        <Field name={subjectFieldNames.organizationGuid}>{() => null}</Field>
        <Field name={subjectFieldNames.guid}>{() => null}</Field>

        <Row>
          <FieldBlock
            label={getTitleFieldWithRedStar({
              label: subjectTypeLabel || '',
              field: subjectFieldNames.organizationType,
              isDecision: true,
            })}
            size="half"
            color="black"
          >
            <SelectInputField
              id={subjectTypeBlockId}
              name={subjectFieldNames.organizationType}
              options={subjectTypeOptions}
              size="large"
              onChange={subjectTypeChange}
              disabled={isGeneralFieldDisabled}
            />
          </FieldBlock>
        </Row>
        {organizationType && (
          <>
            {!isIFIZ && !isIUR && (
              <Row margin="none">
                {(isUR || isIP) && (
                  <FieldBlock
                    label={`Основной государственный регистрационный номер ${isIP ? '(ОГРНИП)' : '(ОГРН)'}`}
                    size="half"
                    color="black"
                    id={ogrnLabelBlockId}
                  />
                )}
                <FieldBlock
                  label={getTitleFieldWithRedStar({
                    label: 'Идентификационный номер налогоплательщика (ИНН)',
                    field: subjectFieldNames.inn,
                    isDecision: true,
                  })}
                  size="half"
                  color="black"
                  id={innLabelBlockId}
                />
                {isFIZ && (
                  <FieldBlock
                    label="Страховой номер индивидуального лицевого счета (СНИЛС)"
                    size="half"
                    color="black"
                    id={snilsBlockId}
                  />
                )}
                {isFIUR && (
                  <FieldBlock
                    label="Номер записи об аккредитации в РАФП"
                    size="half"
                    color="black"
                    id={rafpNzaBlockId}
                  />
                )}
              </Row>
            )}
            {!isIFIZ && !isIUR && (
              <Row>
                {(isUR || isIP) && (
                  <FieldBlock size="half">
                    <KnmOgrnField
                      name={subjectFieldNames.ogrn}
                      clearable={organizationAutocomplete}
                      disabled={isGeneralFieldDisabled || organizationAutocomplete}
                      ogrn={ogrn}
                      onAutoCompleteSelect={handleLegalEntityAutoCompleteSelect}
                      onClear={handleAutoCompleteClear}
                      onLoadingChange={setFnsLookupByOgrnLoadingFlag}
                    />
                  </FieldBlock>
                )}
                <FieldBlock size="half">
                  <KnmInnField
                    name={subjectFieldNames.inn}
                    clearable={organizationAutocomplete}
                    disabled={isInnDisabled}
                    inn={inn}
                    onAutoCompleteSelect={handleLegalEntityAutoCompleteSelect}
                    setLookupData={setLookupData}
                    onClear={handleAutoCompleteClear}
                    onLoadingChange={setFnsLookupByInnLoadingFlag}
                    isFiz={isFIZ}
                    warning={isInnWarning}
                    isShowAutoCompleteField={isShowAutoCompleteField}
                    setCheckByInnMessage={!ervkId ? setCheckByInnMessage : undefined}
                    ervkId={ervkId}
                    subjectType={organizationType}
                    checkSubjectResult={checkSubjectResult}
                  />
                  {isRafpNzaNotFound && inn && kpp && <div className={styles.RafpNotFound}>{rafpNzaNotFoundText}</div>}
                </FieldBlock>
                {isFIZ && (
                  <FieldBlock size="half">
                    <KnmSnilsField
                      name={subjectFieldNames.snils}
                      warning={innOrSnilsWarning}
                      disabled={isGeneralFieldDisabled}
                    />
                  </FieldBlock>
                )}
                {isFIUR && (
                  <FieldBlock size="half">
                    <TextInputField
                      name={subjectFieldNames.rafpNza}
                      disabled={isWatchOnly || !!inn || !!kpp || (!isRafpNzaNotFound && isRafpNzaSent)}
                      warning={rafpWarning}
                      onChange={handleRafpNzaChange}
                    />
                    {isRafpNzaNotFound && !inn && !kpp && (
                      <div className={styles.RafpNotFound}>{rafpNzaNotFoundText}</div>
                    )}
                    {isRafpNzaError && (
                      <Hint
                        text="Запрашиваемые сведения не могут быть предоставлены в настоящий момент. Автоматический запрос
                        будет произведен через час"
                      />
                    )}
                  </FieldBlock>
                )}
              </Row>
            )}
            {isFIUR && (
              <Row>
                <FieldBlock label="Код причины постановки на учет (КПП)" size="half" color="black" id={kppBlockId}>
                  <TextInputField
                    name={subjectFieldNames.kpp}
                    disabled={isWatchOnly || !!rafpNza}
                    warning={rafpWarning}
                  />
                </FieldBlock>
              </Row>
            )}
            {(isFIZ || isIFIZ) && (
              <Row>
                <FieldBlock label="Фамилия, имя, отчество (при наличии)" color="black" id={organizationNameBlockId}>
                  <TextareaField
                    disabled={isGeneralFieldDisabled}
                    name={subjectFieldNames.originalName}
                    resize="none"
                    inputClassName={styles.OrganizationNameInput}
                    parse={normalizeFio}
                  />
                </FieldBlock>
              </Row>
            )}
            {(isUR || isIP || isFIUR) && (
              <Row>
                <FieldBlock
                  id={organizationNameBlockId}
                  label={isFIUR ? 'Наименование организации' : 'Наименование проверяемого лица'}
                  color="black"
                >
                  <TextareaField
                    id={organizationNameBlockId}
                    name={subjectFieldNames.originalName}
                    resize="none"
                    inputClassName={styles.OrganizationNameInput}
                    clearable={isOrganizationNameFieldEnabled && organizationAutocomplete}
                    disabled={!isOrganizationNameFieldEnabled}
                    onClear={handleAutoCompleteClear}
                  />
                  {!smevRequestStatus &&
                    isOrganizationNameFnsError(checkSubjectResult) &&
                    renderFnsErvkEqualsError(
                      checkSubjectResult,
                      lookupData,
                      organizationAutocomplete,
                      styles.FnsMessage,
                    )}
                </FieldBlock>
              </Row>
            )}
            {isIUR && (
              <Row>
                <FieldBlock
                  id={organizationNameBlockId}
                  label={getTitleFieldWithRedStar({
                    label: 'Наименование',
                    field: subjectFieldNames.originalName,
                    isDecision: true,
                  })}
                  color="black"
                >
                  <TextInputField
                    id={organizationNameBlockId}
                    name={subjectFieldNames.originalName}
                    disabled={isGeneralFieldDisabled}
                  />
                </FieldBlock>
              </Row>
            )}
            {(isUR || isFIUR) && (
              <Row>
                {!isLaterThanHideFields4129 ? (
                  <FieldBlock label="Адрес места нахождения" color="black">
                    <TextInputField
                      name={isUR ? subjectFieldNames.locationAddress : subjectFieldNames.factualAddress}
                      disabled={true}
                    />
                  </FieldBlock>
                ) : (
                  <FieldBlock
                    label={
                      <>
                        Адрес места нахождения
                        <ErknmFieldMark fieldName={subjectFieldNames.locationAddress} />
                      </>
                    }
                    color="black"
                  >
                    <KnmFiasAddressField
                      id={subjectFieldNames.locationAddress}
                      addressFieldPathWithPrefix={subjectFieldNames.locationAddress}
                      disabled={true}
                      formRef={formRef}
                      prefixNameNotInFias={subjectFieldNames.location}
                      hideManualAddress={!!ervkId || organizationAutocomplete}
                      manualAddressPlaceholder={
                        <>
                          Место
                          <ErknmFieldMark fieldName={subjectFieldNames.locationmanualAddress} />
                        </>
                      }
                      regionPlaceholder={
                        <>
                          Субъект РФ
                          <ErknmFieldMark fieldName={subjectFieldNames.locationregion} />
                        </>
                      }
                      fieldPaths={{
                        notInFIAS: subjectFieldNames.locationnotInFIAS,
                        isErrorFiasValue: subjectFieldNames.locationisErrorFiasValue,
                      }}
                    />
                  </FieldBlock>
                )}
                {!smevRequestStatus &&
                  ((isUR && isFnsAddressFnsError(checkSubjectResult)) ||
                    (!isUR && isWorkAddressFnsError(checkSubjectResult))) &&
                  renderFnsErvkEqualsError(checkSubjectResult, lookupData, organizationAutocomplete, styles.FnsMessage)}
              </Row>
            )}
            {isUR && !isLaterThanHideFields4129 && (
              <Row>
                <FieldBlock label="Место нахождения (осуществления деятельности) контролируемого лица" color="black">
                  <TextInputField name={subjectFieldNames.workAddress} disabled={true} />
                  {!smevRequestStatus &&
                    isWorkAddressFnsError(checkSubjectResult) &&
                    renderFnsErvkEqualsError(
                      checkSubjectResult,
                      lookupData,
                      organizationAutocomplete,
                      styles.FnsMessage,
                    )}
                </FieldBlock>
              </Row>
            )}
            {isIP && !isLaterThanHideFields4129 && (
              <Row>
                <FieldBlock label="Место фактического осуществления деятельности" color="black">
                  <TextInputField
                    name={subjectFieldNames.factualAddress}
                    disabled={isGeneralFieldDisabled || organizationAutocomplete}
                  />
                  {!smevRequestStatus &&
                    isWorkAddressFnsError(checkSubjectResult) &&
                    renderFnsErvkEqualsError(
                      checkSubjectResult,
                      lookupData,
                      organizationAutocomplete,
                      styles.FnsMessage,
                    )}
                </FieldBlock>
              </Row>
            )}
            {isIFIZ && (
              <Row>
                <FieldBlock label="Сведения о документе, удостоверяющем личность" color="black" id={documentBlockId}>
                  <TextInputField
                    name={subjectFieldNames.document}
                    parse={parsePassport}
                    disabled={isGeneralFieldDisabled}
                  />
                </FieldBlock>
              </Row>
            )}
            {(isIFIZ || isIUR) && (
              <Row>
                <FieldBlock
                  label={getTitleFieldWithRedStar({
                    label: isIFIZ
                      ? 'Гражданство (при наличии)'
                      : 'Государство, в котором указанное юридическое лицо зарегистрировано',
                    field: subjectFieldNames.country,
                    isDecision: true,
                  })}
                  color="black"
                  id={countryBlockId}
                >
                  <SelectInputFieldAutoOptions
                    id={countryBlockId}
                    name={subjectFieldNames.country}
                    isClearable={false}
                    size="medium"
                    optionName={CommonCatalogsNamesEnum.COUNTRIES}
                    transformFunc={isIFIZ ? filterFlCountries : filterUlCountries}
                    disabled={isGeneralFieldDisabled}
                  />
                </FieldBlock>
              </Row>
            )}
            {isUR && (
              <KnmSubjectNkoInfo
                id={name}
                name={name}
                ogrn={ogrn}
                checkByInnMessage={checkByInnMessage}
                formRef={formRef as IKnmFormRef}
                isShowMessages={false}
                ervkId={ervkId}
              />
            )}
            {!isFIZ && !isIFIZ && (
              <KnmMspField
                id={`${name}mspCode`}
                name={`${name}mspCode`}
                value={mspCode}
                checkSubjectResult={checkSubjectResult}
                ervkId={ervkId}
                inn={inn}
                mspRegistryDate={mspRegistryDate}
                formRef={formRef as IKnmFormRef}
                isShowMessages={false}
              />
            )}
            {(isUR || isIP || isFIUR) && (
              <Row>
                <KnmSonkoField
                  inn={inn}
                  ogrn={ogrn}
                  name={subjectFieldNames.isSonko}
                  value={isSonko}
                  subjectType={organizationType}
                  id={subjectFieldNames.isSonko}
                  checkByInnMessage={!ervkId ? checkByInnMessage : undefined}
                  formRef={formRef as IKnmFormRef}
                  isShowMessages={false}
                />
              </Row>
            )}
            {(isUR || isFIUR) && (
              <KnmSubjectItInfo
                name={parentName}
                inn={inn}
                inspectionResults={undefined}
                isPresentInItRegistry={isIt}
                itAccreditationResponse={itAccreditationResponse}
                smevItAccreditationRequestStatus={smevItAccreditationRequestStatus}
                checkByInnMessage={checkByInnMessage}
                isShowMessages={true}
                ervkId={ervkId}
              />
            )}
            <Row>
              <ErknmRegistryCheck
                className={styles.RegistryCheckBlock}
                initialCheckSubjectResult={checkSubjectResult || null}
              />
            </Row>
          </>
        )}
      </>
    );
  },
);

const mapStateToProps = (state: IRootState): IDecisionSubjectItemStoreProps => ({
  districtSelectOptions: getDistrictSelectOptions(state),
  settings: getAdminFetchSettings(state),
  created: getKnmDataCreated(state),
  decisionDataCreated: getDecisionDataCreatedAt(state),
});

export const DecisionSubjectItem = connect(mapStateToProps, null, null, { forwardRef: true })(
  DecisionSubjectItemWithoutStore,
);
